import { eManulSortLayerDrawOrder, Game } from "../../Game";
import { SmoothValue } from "../../Utils/SmoothValue";
import { TIMER } from "../Gameplay";
import { Ball } from "./Ball";


/**
 * Seznam vrcholu ocasu. Jedna se vlastne o jednotlive pozice, po kterych se pohybuje mic.
 */
class TailVertices {
    private static readonly BUF_POS_X = 0;
    private static readonly BUF_POS_Y = 1;
    private static readonly BUF_POS_Z = 2;

    private static readonly ENTRY_SIZE = 3;

    private _maxVertexCnt: number;
    private _buffer: Float32Array;
    private _bufferStartPos: number;
    private _bufferVertexCnt: number;

    private _vertex = new pc.Vec3();

    /**
     * (RO) Max pocet vrcholu v bufferu
     */
    get maxVertexCnt(): number { return this._maxVertexCnt; }

    /**
     * (RO) Aktualni celkovy pocet vrcholu v bufferu
     */
    get vertexCnt(): number { return this._bufferVertexCnt; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(maxVertexCnt: number) {
        this._maxVertexCnt = maxVertexCnt;
        this._buffer = new Float32Array(maxVertexCnt * TailVertices.ENTRY_SIZE);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._bufferStartPos = 0;
        this._bufferVertexCnt = 0;
    }

    //-------------------------------------------------------------------------
    /**
     * Pridat vrchol ocasu
     * @param pos 
     */
    //-------------------------------------------------------------------------
    addVertex(pos: pc.Vec3): void {
        let bufPos = this._bufferStartPos + this._bufferVertexCnt * TailVertices.ENTRY_SIZE;

        if (bufPos >= this._buffer.length)
            bufPos -= this._buffer.length;

        this._buffer[bufPos + TailVertices.BUF_POS_X] = pos.x;
        this._buffer[bufPos + TailVertices.BUF_POS_Y] = pos.y;
        this._buffer[bufPos + TailVertices.BUF_POS_Z] = pos.z;

        if (this._bufferVertexCnt < this._maxVertexCnt) {
            this._bufferVertexCnt++;
        }
        else {
            if ((this._bufferStartPos += TailVertices.ENTRY_SIZE) == this._buffer.length)
                this._bufferStartPos = 0;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci vrchol s danym indexem. Posledne pridany vrchol ma nejvyssi index.
     * @param id 
     * @returns 
     */
    //-------------------------------------------------------------------------
    getVertex(id: number): pc.Vec3 {
        let bufPos = this._bufferStartPos + id * TailVertices.ENTRY_SIZE;

        if (bufPos >= this._buffer.length)
            bufPos -= this._buffer.length;

        return this._vertex.set(this._buffer[bufPos + TailVertices.BUF_POS_X], this._buffer[bufPos + TailVertices.BUF_POS_Y], this._buffer[bufPos + TailVertices.BUF_POS_Z]);
    }
}



class TailPart {
    /**
     * Pocet prvku urcujicich vrchol (x, y, z = 3)
     */
    private static readonly VERTEX_ELEMENT_CNT = 3;

    /**
     * Vrcholy ocasu
     */
    private _tailVertices: TailVertices;
    /**
     * Max pocet vrcholu, ze kterych se TailPart bude vytvaret. Napr. maximalni pocet vrchlu v _tailVertices je 10. _lenghtRatio je 0.5, tzn. ze se TailPart bude vytvaret z poslednich 5 vrcholu v _tailVertices.
     */
    private _maxTailVertices: number;
    /**
     * Maximalni rirka TailPart (sirka u nejnovejsiho vrcholu)
     */
    private _startWidth: number;
    /**
     * Minimalni sirka TailPart (sirka u nejstarsiho vrcholu)
     */
    private _endWidth: number;

    private _xOffset: number;
    private _yOffset: number;

    private _vertexData: Float32Array;
    private _vertexIndices: Uint16Array;
    private _mesh: pc.Mesh;
    private _entity: pc.Entity;

    get entity(): pc.Entity { return this._entity; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(tailVertices: TailVertices, lenRatio: number, startWidth: number, endWidth: number, xOffset: number, yOffset: number, material: pc.Material, vertexIndices: Uint16Array) {
        this._tailVertices = tailVertices;
        this._maxTailVertices = Math.max(2, Math.round(tailVertices.maxVertexCnt * lenRatio));

        this._startWidth = startWidth;
        this._endWidth = endWidth;

        this._xOffset = xOffset;
        this._yOffset = yOffset;

        let vertexCnt = this._maxTailVertices * 2 - 1;

        this._vertexData = new Float32Array(vertexCnt * TailPart.VERTEX_ELEMENT_CNT);
        this._vertexIndices = vertexIndices;

        this._mesh = new pc.Mesh(Game.app.graphicsDevice);
        this._mesh.clear(true, false);
        this._mesh.setPositions(this._vertexData, TailPart.VERTEX_ELEMENT_CNT, 0);
        this._mesh.setIndices(this._vertexIndices, 0);
        this._mesh.update(pc.PRIMITIVE_TRISTRIP);

        // Create the mesh instance
        let node = new pc.GraphNode();
        let meshInstance = new pc.MeshInstance(this._mesh, material, node);
        meshInstance.drawOrder = eManulSortLayerDrawOrder.ballTail;

        // Create a model and add the mesh instance to it
        let model = new pc.Model();
        model.graph = node;
        model.meshInstances = [meshInstance];

        // Add a model compoonent
        this._entity = new pc.Entity();
        this._entity.addComponent('model', {
            type: 'asset',
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            layers: [Game.manualSortLayer.id]
        });
        this._entity.model.model = model;
        Game.app.root.addChild(this._entity);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._entity.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    create(): void {
        // V bufferu musi byt dostatecny pocet vrcholu
        let vertexCnt = this._tailVertices.vertexCnt - (this._tailVertices.maxVertexCnt - this._maxTailVertices);

        if (vertexCnt < 2) {
            // Neni dostatek vrcholu
            this._entity.enabled = false;
            return;
        }

        // Vytvorit jednotlive vrcholy pro mesh TailPart
        let hW = this._startWidth / 2;
        let hWDecStep = ((this._startWidth - this._endWidth) / (vertexCnt - 2)) / 2;

        let data = this._vertexData;
        let dataPos = 0;

        let vertexId = this._tailVertices.vertexCnt - 1;

        while (vertexCnt-- != 0) {
            let vertex = this._tailVertices.getVertex(vertexId--);

            if (vertexCnt != 0) {
                data[dataPos++] = vertex.x + this._xOffset - hW;
                data[dataPos++] = vertex.y + this._yOffset;
                data[dataPos++] = vertex.z;
                data[dataPos++] = vertex.x + this._xOffset + hW;
                data[dataPos++] = vertex.y + this._yOffset;
                data[dataPos++] = vertex.z;
            }
            else {
                data[dataPos++] = vertex.x + this._xOffset;
                data[dataPos++] = vertex.y + this._yOffset;
                data[dataPos++] = vertex.z;
            }

            // Dalsi vrcholy budou blize k sobe
            hW -= hWDecStep;
        }

        // Aktualizovat mesh
        vertexCnt = dataPos / 3;

        this._mesh.setPositions(this._vertexData, TailPart.VERTEX_ELEMENT_CNT, vertexCnt);
        this._mesh.setIndices(this._vertexIndices, vertexCnt);
        this._mesh.update(pc.PRIMITIVE_TRISTRIP);

        this._entity.enabled = true;
    }
}



const enum eTailState {
    active,
    hiding,
    hidden,
}



interface TailSettings {
    opacity: number;
    color: pc.Color;
}



export class BallTail {
    private static readonly MAX_VERTEX_CNT = 20;

    private static readonly SETTINGS: TailSettings[] = [
        // Obyc mic
        { opacity: 0.3, color: new pc.Color(1, 1, 1) },
        // Fireball
        { opacity: 0.4, color: new pc.Color().fromString("#eac405") },
    ];

    private _state: eTailState;
    private _vertices: TailVertices;
    private _parts: TailPart[];
    private _material: pc.StandardMaterial;
    private _opacity: SmoothValue;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this._vertices = new TailVertices(BallTail.MAX_VERTEX_CNT);

        // Material
        this._opacity = new SmoothValue();

        let material = new pc.StandardMaterial();

        material = new pc.StandardMaterial();
        material.cull = pc.CULLFACE_NONE;
        material.blendType = pc.BLEND_ADDITIVEALPHA;
        material.diffuse.set(0, 0, 0);
        material.emissive.set(1, 1, 1);
        material.opacity = 0.5;
        material.useLighting = false;
        material.update();

        // Jednotlive casti
        let maxVertexCnt = BallTail.MAX_VERTEX_CNT * 2 - 1;
        let vertexIndices = new Uint16Array(maxVertexCnt);

        for (let i = 0; i < maxVertexCnt; i++)
            vertexIndices[i] = i;

        this._parts = [
            new TailPart(this._vertices, 0.6, (Ball.RADIUS * 2) * 0.9, (Ball.RADIUS * 2) * 0.4, 0, 0, material, vertexIndices),
            //new TailPart(this._vertices, 0.3, (Ball.RADIUS * 2) * 0.7, (Ball.RADIUS * 2) * 0.3, 0, 0.001, material, vertexIndices),
            new TailPart(this._vertices, 1, 0.015, 0.001, -Ball.RADIUS * 0.8, 0, material, vertexIndices),
            new TailPart(this._vertices, 1, 0.015, 0.001, Ball.RADIUS * 0.8, 0, material, vertexIndices),
            new TailPart(this._vertices, 0.9, 0.015, 0.001, 0, 0, material, vertexIndices),
        ];

        this._material = this._parts[0].entity.model.meshInstances[0].material as pc.StandardMaterial;
        this._state = eTailState.active;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._vertices.reset();
        this._parts.forEach((part: TailPart): void => { part.reset(); });

        let settings = BallTail.SETTINGS[Ball.instance.fireBallState != 1 ? 0 : 1];

        this._opacity.value = settings.opacity;
        this._material.opacity = settings.opacity;
        this._material.emissive.copy(settings.color);
        this._material.update();

        this._state = eTailState.active;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    addVertex(pos: pc.Vec3): void {
        if (this._state != eTailState.hidden) {
            // Pridat vrchol ocasu do seznamu vrcholu
            this._vertices.addVertex(pos);

            this._parts.forEach((part: TailPart): void => { part.create(); });
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    hide(): void {
        if (this._state == eTailState.active) {
            this._state = eTailState.hiding;

            this._opacity.startChange(TIMER.time, 0, pc.Linear, 500);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._state == eTailState.hiding) {
            let mat = this._material;

            mat.opacity = this._opacity.update(TIMER.time);
            mat.update();

            if (!this._opacity.isValueChanging) {
                this._parts.forEach((part: TailPart): void => { part.reset(); });
                this._state = eTailState.hidden;
            }
        }
    }
}